import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  MessageSquare,
  UserPlus,
  FileEdit,
  Trash2,
  AlertTriangle,
  Flag,
  ListTodo,
  DollarSign,
  Calendar,
  Settings,
  Filter,
  Search,
  MoreVertical,
  Circle,
  Mail,
  AtSign,
  ArrowRight,
  Eye,
  X,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

// Types
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  actionUrl?: string;
  actor?: {
    id: string;
    name: string;
    avatar?: string;
  };
  metadata?: Record<string, any>;
}

export type NotificationType =
  | 'task_assigned'
  | 'task_completed'
  | 'task_comment'
  | 'task_due_soon'
  | 'task_overdue'
  | 'mention'
  | 'member_joined'
  | 'member_left'
  | 'milestone_created'
  | 'milestone_completed'
  | 'milestone_due'
  | 'project_created'
  | 'project_updated'
  | 'project_member_added'
  | 'project_member_removed'
  | 'budget_alert'
  | 'risk_alert'
  | 'system';

export interface Activity {
  id: string;
  type: ActivityType;
  description: string;
  createdAt: string;
  actor: {
    id: string;
    name: string;
    avatar?: string;
  };
  metadata?: Record<string, any>;
}

export type ActivityType =
  | 'task_created'
  | 'task_updated'
  | 'task_completed'
  | 'task_deleted'
  | 'comment_added'
  | 'member_added'
  | 'member_removed'
  | 'milestone_created'
  | 'milestone_completed'
  | 'budget_updated'
  | 'risk_added'
  | 'project_created'
  | 'project_updated';

export interface NotificationSettings {
  emailEnabled: boolean;
  pushEnabled: boolean;
  categories: {
    tasks: boolean;
    mentions: boolean;
    milestones: boolean;
    budget: boolean;
    risks: boolean;
    team: boolean;
  };
}

interface NotificationsPanelProps {
  notifications: Notification[];
  activities: Activity[];
  settings: NotificationSettings;
  onMarkRead?: (notificationId: string) => void;
  onMarkAllRead?: () => void;
  onDelete?: (notificationId: string) => void;
  onUpdateSettings?: (settings: NotificationSettings) => void;
  onNotificationClick?: (notification: Notification) => void;
}

// Type configurations
const notificationTypeConfig: Record<NotificationType, { icon: React.ReactNode; color: string; bg: string }> = {
  task_assigned: { icon: <ListTodo size={16} />, color: '#ffffff', bg: '#4F46E5' },
  task_completed: { icon: <Check size={16} />, color: '#22c55e', bg: '#dcfce7' },
  task_comment: { icon: <MessageSquare size={16} />, color: '#8b5cf6', bg: '#ede9fe' },
  task_due_soon: { icon: <Clock size={16} />, color: '#f59e0b', bg: '#fef3c7' },
  task_overdue: { icon: <AlertTriangle size={16} />, color: '#ef4444', bg: '#fee2e2' },
  mention: { icon: <AtSign size={16} />, color: '#ec4899', bg: '#fce7f3' },
  member_joined: { icon: <UserPlus size={16} />, color: '#10b981', bg: '#d1fae5' },
  member_left: { icon: <UserPlus size={16} />, color: '#6b7280', bg: '#f3f4f6' },
  milestone_created: { icon: <Flag size={16} />, color: '#ffffff', bg: '#4F46E5' },
  milestone_completed: { icon: <Flag size={16} />, color: '#22c55e', bg: '#dcfce7' },
  milestone_due: { icon: <Flag size={16} />, color: '#f59e0b', bg: '#fef3c7' },
  project_created: { icon: <FileEdit size={16} />, color: '#8b5cf6', bg: '#ede9fe' },
  project_updated: { icon: <FileEdit size={16} />, color: '#ffffff', bg: '#4F46E5' },
  project_member_added: { icon: <UserPlus size={16} />, color: '#10b981', bg: '#d1fae5' },
  project_member_removed: { icon: <UserPlus size={16} />, color: '#6b7280', bg: '#f3f4f6' },
  budget_alert: { icon: <DollarSign size={16} />, color: '#ef4444', bg: '#fee2e2' },
  risk_alert: { icon: <AlertTriangle size={16} />, color: '#f59e0b', bg: '#fef3c7' },
  system: { icon: <Bell size={16} />, color: '#6b7280', bg: '#f3f4f6' },
};

const activityTypeConfig: Record<ActivityType, { icon: React.ReactNode; color: string }> = {
  task_created: { icon: <ListTodo size={14} />, color: '#4F46E5' },
  task_updated: { icon: <FileEdit size={14} />, color: '#8b5cf6' },
  task_completed: { icon: <Check size={14} />, color: '#22c55e' },
  task_deleted: { icon: <Trash2 size={14} />, color: '#ef4444' },
  comment_added: { icon: <MessageSquare size={14} />, color: '#ec4899' },
  member_added: { icon: <UserPlus size={14} />, color: '#10b981' },
  member_removed: { icon: <UserPlus size={14} />, color: '#6b7280' },
  milestone_created: { icon: <Flag size={14} />, color: '#f59e0b' },
  milestone_completed: { icon: <Flag size={14} />, color: '#22c55e' },
  budget_updated: { icon: <DollarSign size={14} />, color: '#4F46E5' },
  risk_added: { icon: <AlertTriangle size={14} />, color: '#f59e0b' },
  project_created: { icon: <FileEdit size={14} />, color: '#8b5cf6' },
  project_updated: { icon: <FileEdit size={14} />, color: '#8b5cf6' },
};

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  activities,
  settings,
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onUpdateSettings,
  onNotificationClick,
}) => {
  // State
  const [activeTab, setActiveTab] = useState<'notifications' | 'activity' | 'settings'>('notifications');
  const [filterType, setFilterType] = useState<string>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  // Stats
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    let result = [...notifications];
    
    if (showUnreadOnly) {
      result = result.filter((n) => !n.read);
    }
    
    if (filterType !== 'all') {
      result = result.filter((n) => n.type === filterType);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (n) =>
          n.title.toLowerCase().includes(query) ||
          n.message.toLowerCase().includes(query)
      );
    }
    
    return result.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)));
  }, [notifications, showUnreadOnly, filterType, searchQuery]);

  // Group activities by date
  const groupedActivities = useMemo(() => {
    const groups: Record<string, Activity[]> = {};
    
    activities
      .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
      .forEach((activity) => {
        const date = dayjs(activity.createdAt).format('YYYY-MM-DD');
        const label = dayjs(date).isToday()
          ? 'Today'
          : dayjs(date).isYesterday()
          ? 'Yesterday'
          : dayjs(date).format('MMM D, YYYY');
        
        if (!groups[label]) {
          groups[label] = [];
        }
        groups[label].push(activity);
      });
    
    return groups;
  }, [activities]);

  // Handlers
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkRead?.(notification.id);
    }
    onNotificationClick?.(notification);
  };

  const handleToggleSetting = (
    category: keyof NotificationSettings['categories'] | 'emailEnabled' | 'pushEnabled',
    value: boolean
  ) => {
    if (category === 'emailEnabled' || category === 'pushEnabled') {
      onUpdateSettings?.({
        ...settings,
        [category]: value,
      });
    } else {
      onUpdateSettings?.({
        ...settings,
        categories: {
          ...settings.categories,
          [category]: value,
        },
      });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Notifications & Activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stay updated on project changes
          </Typography>
        </Box>
        {activeTab === 'notifications' && unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<CheckCheck size={16} />}
            onClick={onMarkAllRead}
          >
            Mark all read
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab
          value="notifications"
          label={
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: unreadCount > 0 ? 2 : 0 }}>
                <Bell size={16} />
                Notifications
              </Box>
            </Badge>
          }
        />
        <Tab
          value="activity"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={16} />
              Activity
            </Box>
          }
        />
        <Tab
          value="settings"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings size={16} />
              Settings
            </Box>
          }
        />
      </Tabs>

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  size="small"
                />
              }
              label="Unread only"
            />
          </Box>

          {/* Notification List */}
          {filteredNotifications.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <Bell size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showUnreadOnly ? 'No unread notifications' : 'You\'re all caught up!'}
              </Typography>
            </Card>
          ) : (
            <List disablePadding>
              {filteredNotifications.map((notification, index) => {
                const config = notificationTypeConfig[notification.type];
                
                return (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        bgcolor: notification.read ? 'transparent' : '#ececed',
                        borderRadius: 1,
                        mb: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: notification.read ? '#f9fafb' : '#d6d7d9',
                        },
                      }}
                      onClick={() => handleNotificationClick(notification)}
                      secondaryAction={
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                            setSelectedNotification(notification);
                          }}
                        >
                          <MoreVertical size={16} />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        {notification.actor ? (
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  bgcolor: config.bg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid white',
                                  color: config.color,
                                }}
                              >
                                {config.icon}
                              </Box>
                            }
                          >
                            <Avatar src={notification.actor.avatar} sx={{ width: 40, height: 40 }}>
                              {notification.actor.name.charAt(0)}
                            </Avatar>
                          </Badge>
                        ) : (
                          <Avatar sx={{ width: 40, height: 40, bgcolor: config.bg }}>
                            <Box sx={{ color: config.color }}>{config.icon}</Box>
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={notification.read ? 400 : 600}
                            >
                              {notification.title}
                            </Typography>
                            {!notification.read && (
                              <Circle size={8} fill="#4F46E5" color="#4F46E5" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(notification.createdAt).fromNow()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredNotifications.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <Box>
          {Object.keys(groupedActivities).length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <Clock size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No activity yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Project activity will appear here
              </Typography>
            </Card>
          ) : (
            Object.entries(groupedActivities).map(([date, dateActivities]) => (
              <Box key={date} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {date}
                </Typography>
                <Paper variant="outlined">
                  {dateActivities.map((activity, index) => {
                    const config = activityTypeConfig[activity.type];
                    
                    return (
                      <React.Fragment key={activity.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: config.color + '20',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: config.color,
                              flexShrink: 0,
                            }}
                          >
                            {config.icon}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2">
                              <strong>{activity.actor.name}</strong>{' '}
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(activity.createdAt).format('h:mm A')}
                            </Typography>
                          </Box>
                          <Avatar
                            src={activity.actor.avatar}
                            sx={{ width: 24, height: 24 }}
                          >
                            {activity.actor.name.charAt(0)}
                          </Avatar>
                        </Box>
                        {index < dateActivities.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </Paper>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Box>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Delivery Channels
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose how you want to receive notifications
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailEnabled}
                      onChange={(e) => handleToggleSetting('emailEnabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Mail size={16} />
                      Email notifications
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.pushEnabled}
                      onChange={(e) => handleToggleSetting('pushEnabled', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Bell size={16} />
                      Push notifications
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Notification Categories
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which types of notifications you want to receive
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.tasks}
                      onChange={(e) => handleToggleSetting('tasks', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Tasks</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Task assignments, completions, and due date reminders
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.mentions}
                      onChange={(e) => handleToggleSetting('mentions', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Mentions</Typography>
                      <Typography variant="caption" color="text.secondary">
                        When someone mentions you in comments
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.milestones}
                      onChange={(e) => handleToggleSetting('milestones', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Milestones</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Milestone completions and due date alerts
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.budget}
                      onChange={(e) => handleToggleSetting('budget', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Budget</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Budget alerts and spending updates
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.risks}
                      onChange={(e) => handleToggleSetting('risks', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Risks</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Risk alerts and status changes
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.categories.team}
                      onChange={(e) => handleToggleSetting('team', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Team</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Team member joins and role changes
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setSelectedNotification(null);
        }}
      >
        {selectedNotification && !selectedNotification.read && (
          <MenuItem
            onClick={() => {
              onMarkRead?.(selectedNotification.id);
              setMenuAnchor(null);
            }}
          >
            <Check size={16} style={{ marginRight: 8 }} />
            Mark as read
          </MenuItem>
        )}
        {selectedNotification?.actionUrl && (
          <MenuItem
            onClick={() => {
              handleNotificationClick(selectedNotification);
              setMenuAnchor(null);
            }}
          >
            <Eye size={16} style={{ marginRight: 8 }} />
            View details
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedNotification) {
              onDelete?.(selectedNotification.id);
            }
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NotificationsPanel;
